import {IFormulaExpression, ISheetAPI, ISheetValue, NamedArguments} from '@mintdata/mint-kit/api/functions/SheetAPI';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {AInteger} from '@mintdata/mint-kit/api/values/AInteger';
import {IFrame, IFrameField} from '@mintdata/mint-kit/api/values/frames/Frame';
import {FrameField} from '@mintdata/mint-kit/api/values/frames/FrameField';
import {Frames} from '@mintdata/mint-kit/api/values/frames/Frames';
import {IFrameRowsVisitor} from '@mintdata/mint-kit/api/values/frames/FrameVisitor';
import bind from 'bind-decorator';

export enum IJsonPathExpressionType
{
    Root = 'root',
    Identifier = 'identifier',
    NumericLiteral = 'numeric_literal',
    Wildcard = 'wildcard',
    Slice = 'slice',
    Union = 'union',
    FilterExpression = 'filter_expression',
    ScriptExpression = 'script_expression'
}

export enum IJsonPathScopeType
{
    Child = 'child',
    Descendant = 'descendant'
}

export enum IJsonPathOperationType
{
    Member = 'member',
    Subscript = 'subscript'
}

export interface IJsonPath
{
    readonly expression:{
        readonly type:IJsonPathExpressionType;
        readonly value:string | number;
    };
    readonly scope?:IJsonPathScopeType;
    readonly operation:IJsonPathOperationType;
}

export class FrameAugmentVisitor implements IFrameRowsVisitor
{
    private _fields:Array<IFrameField> = [];
    private _data:Array<Array<IValue>> = [];
    private _frame:IFrame = null;
    private _rowIndexFrom:number = 0;
    private _rowIndexTo:number = -1;
    private _rowStep:number = 1;
    private _fieldName:string = null;
    private _colIndex:number = null;
    private _rowArgName:string = `@${FrameAugmentVisitor.ROW_PARAM}`;

    public static readonly WILDCARD:string = '*';
    public static readonly ROW_PARAM:string = 'row';

    public constructor(private readonly _api:ISheetAPI,
                       private _jsonPaths:Array<IJsonPath>,
                       private readonly _valueExpr:IFormulaExpression,
                       private readonly _parentNamedArguments:NamedArguments)
    {
    }

    @bind
    public getResult():IFrame
    {
        return Frames.fromFieldsAndData(this._fields, this._data);
    }

    @bind
    public async preFrame(frame:IFrame):Promise<any>
    {
        this._frame = frame;

        let currentPath:IJsonPath = this._jsonPaths[0];
        const rowsCount:number = frame.getRowsCount();

        if( currentPath.operation === IJsonPathOperationType.Subscript )
        {
            if( currentPath.scope === IJsonPathScopeType.Child )
            {
                if( currentPath.expression.type === IJsonPathExpressionType.Slice )
                {
                    const spliceParams:Array<number> = (currentPath.expression.value as string)
                        .split(':')
                        .map(
                            (value:string):number => value.match(/^-?[0-9]+$/)
                                ? parseInt(value)
                                : null
                        );

                    this._rowIndexFrom = spliceParams[0] >= 0
                        ? spliceParams[0]
                        : (rowsCount + spliceParams[0] - 1);

                    if( spliceParams[1] != null )
                        this._rowIndexTo = spliceParams[1] >= 0
                            ? spliceParams[1]
                            : (rowsCount + spliceParams[1] - 1);

                    if( spliceParams[2] != null && spliceParams[2] > 0 )
                        this._rowStep = spliceParams[2];

                    this._jsonPaths.shift();
                }
                else if( currentPath.expression.type === IJsonPathExpressionType.NumericLiteral )
                {
                    this._rowIndexFrom = currentPath.expression.value as number;
                    this._rowIndexTo = this._rowIndexFrom;

                    this._jsonPaths.shift();
                }
            }
        }
        else if( currentPath.operation === IJsonPathOperationType.Member
            && currentPath.scope === IJsonPathScopeType.Descendant )
            this._rowIndexTo = rowsCount - 1;

        currentPath = this._jsonPaths[0];

        if( currentPath
            && currentPath.operation === IJsonPathOperationType.Member
            && (currentPath.expression.type === IJsonPathExpressionType.Identifier
                || currentPath.expression.type === IJsonPathExpressionType.NumericLiteral
                || currentPath.expression.type === IJsonPathExpressionType.Wildcard) )
        {
            this._fieldName = String(currentPath.expression.value);

            this._jsonPaths.shift();
        }
    }

    @bind
    public async postFrame(frame:IFrame):Promise<any>
    {
        if( this._isNeedAddField() )
            this._fields.push(new FrameField(this._fieldName, Type.String));

        this._frame = null;
    }

    @bind
    public async frameField(field:IFrameField, colIndex:number):Promise<any>
    {
        this._fields[colIndex] = field;

        if( field.name === this._fieldName )
            this._colIndex = colIndex;

        if( field.name === FrameAugmentVisitor.ROW_PARAM )
            this._rowArgName = `@_${FrameAugmentVisitor.ROW_PARAM}`;
    }

    @bind
    public async preFrameRow(rowData:Array<IValue>, rowIndex:number):Promise<any>
    {
        this._data[rowIndex] = [];
    }

    @bind
    public async postFrameRow(rowData:Array<IValue>, rowIndex:number):Promise<any>
    {
        if( rowIndex >= this._rowIndexFrom
            && rowIndex <= this._rowIndexTo
            && this._isLastPath()
            && this._fieldName
            && this._fieldName !== FrameAugmentVisitor.WILDCARD )
        {
            const value:IValue = await this._valueExpr.eval(this._getRowNamedArguments(rowIndex));

            if( this._colIndex != null )
                this._data[rowIndex][this._colIndex] = value;
            else
                this._data[rowIndex][this._fields.length] = value;
        }
    }

    @bind
    public async frameCell(field:IFrameField, value:IValue, colIndex:number, rowIndex:number):Promise<any>
    {
        if( rowIndex < this._rowIndexFrom
            || rowIndex > this._rowIndexTo
            || this._isLastPath()
            || field.name !== this._fieldName
            || this._fieldName === FrameAugmentVisitor.WILDCARD
            || !Type.isInstanceOf(value, Type.Frame) ) // TODO: Implement step support
        {
            if( this._fieldName === FrameAugmentVisitor.WILDCARD )
            {
                const value:IValue = await this._valueExpr.eval(this._getRowNamedArguments(rowIndex));

                this._data[rowIndex][colIndex] = value;
            }
            else
                this._data[rowIndex][colIndex] = value;
        }
        else
        {
            const frameVisitor:FrameAugmentVisitor = new FrameAugmentVisitor(
                this._api,
                this._jsonPaths.slice(),
                this._valueExpr,
                this._getRowNamedArguments(rowIndex)
            );

            await (value as IFrame).acceptAsync(frameVisitor);

            this._data[rowIndex][colIndex] = frameVisitor.getResult();
        }
    }

    private _isLastPath():boolean
    {
        return this._jsonPaths.length === 0;
    }

    private _isNeedAddField():boolean
    {
        return this._isLastPath()
            && this._fieldName
            && this._fieldName !== FrameAugmentVisitor.WILDCARD
            && this._colIndex === null;
    }

    private _getRowNamedArguments(rowIndex:number):NamedArguments
    {
        const namedArguments:NamedArguments = new Map<string, IValue | ISheetValue>(this._parentNamedArguments);

        namedArguments.set(this._rowArgName, AInteger.fromNumber(rowIndex));

        const fields:Array<IFrameField> = this._frame.getFieldsUnsafe();
        const data:Array<Array<IValue>> = this._frame.getDataUnsafe();

        fields.forEach((field:IFrameField, colIndex:number) =>
        {
            namedArguments.set(`@${field.name}`, data[rowIndex][colIndex]);
        });

        return namedArguments;
    }
}
